<template>
  <div class="stocks-modern">
    <div class="stocks-container">
      <!-- Content Section -->
      <div class="stocks-content">
        <!-- Filters Section -->
        <div class="filters-section">
          <div class="filters-card">
            <div class="filters-header">
              <h3 class="filters-title">
                <i class="fas fa-filter mr-2"></i>
                Filtros de Busca
              </h3>
            </div>

            <div class="filters-grid">
              <!-- Tipo -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-tag filter-icon"></i>
                  Tipo
                </label>
                <v-select
                  :items="types"
                  item-text="name"
                  item-value="id"
                  v-model="stock_item_type_id"
                  @change="getStockedItems"
                  outlined
                  dense
                  hide-details
                  class="modern-select"
                />
              </div>

              <!-- Grupo -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-layer-group filter-icon"></i>
                  Grupo
                </label>
                <v-select
                  :items="groups"
                  item-text="name"
                  item-value="id"
                  v-model="stock_group_id"
                  @change="getStockedItems"
                  outlined
                  dense
                  hide-details
                  class="modern-select"
                />
              </div>

              <!-- Categoria -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-list filter-icon"></i>
                  Categoria
                </label>
                <v-select
                  :items="categories"
                  item-text="name"
                  item-value="id"
                  v-model="stock_item_category_id"
                  @change="getStockedItems"
                  outlined
                  dense
                  hide-details
                  class="modern-select"
                />
              </div>

              <!-- Status do Residente -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-user-check filter-icon"></i>
                  Status Residente
                </label>
                <v-select
                  :items="[
                    { id: null, name: 'TODOS' },
                    { id: 'ATIVO', name: 'ATIVO' },
                    { id: 'INATIVO', name: 'INATIVO' },
                  ]"
                  item-text="name"
                  item-value="id"
                  v-model="status_resident"
                  @change="handleChangeStatusResident"
                  outlined
                  dense
                  hide-details
                  class="modern-select"
                />
              </div>

              <!-- Proprietário -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-user filter-icon"></i>
                  Proprietário
                </label>
                <v-select
                  :items="residents"
                  item-text="name"
                  item-value="id"
                  v-model="resident_id"
                  @change="handleChange"
                  outlined
                  dense
                  hide-details
                  class="modern-select"
                />
              </div>

              <!-- Buscar -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-search filter-icon"></i>
                  Buscar
                </label>
                <v-text-field
                  v-model="search"
                  placeholder="Digite o nome do item"
                  outlined
                  dense
                  hide-details
                  class="modern-input"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Table Section -->
        <div class="table-section">
          <div class="table-card">
            <div class="table-header">
              <h3 class="table-title">
                <i class="fas fa-table mr-2"></i>
                Itens em Estoque
              </h3>
              <div class="table-info">
                <span class="item-count">
                  {{ stocked_items.length }}
                  {{ stocked_items.length === 1 ? "item" : "itens" }}
                </span>
              </div>
            </div>

            <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
              <!-- No Results -->
              <div
                v-if="stocked_items.length === 0 && !loading"
                class="no-results"
              >
                <i class="fas fa-search no-results-icon"></i>
                <h4 class="no-results-title">Nenhum item encontrado</h4>
                <p class="no-results-text">
                  Tente ajustar os filtros para encontrar resultados
                </p>
              </div>

              <!-- Data Table -->
              <v-data-table
                v-else
                :search="search"
                :headers="headers"
                :items="stocked_items"
                :footer-props="{
                  'items-per-page-options': [30, 50, 75, 100],
                  'items-per-page-text': 'Mostrar',
                }"
                :options.sync="options"
                class="modern-table"
                :no-data-text="'Nenhum item em estoque'"
                :loading-text="'Carregando itens...'"
              >
                <template v-slot:[`item.name`]="{ item }">
                  <div class="item-name-cell">
                    <div class="item-icon">
                      <i :class="getItemIcon(item)"></i>
                    </div>
                    <div class="item-details">
                      <span class="item-name">{{ item.name }}</span>
                      <span class="item-type">{{
                        item.stock_item_type_name
                      }}</span>
                    </div>
                  </div>
                </template>

                <template v-slot:[`item.stock_group_name`]="{ item }">
                  <span
                    class="group-badge"
                    :class="getGroupBadgeClass(item.stock_group_name)"
                  >
                    {{ item.stock_group_name }}
                  </span>
                </template>

                <template v-slot:[`item.amount`]="{ item }">
                  <div class="amount-cell">
                    <span
                      class="amount-value"
                      :class="getAmountClass(item.total)"
                    >
                      {{
                        item.total.toLocaleString("pt-BR", {
                          minimumIntegerDigits: 1,
                          minimumSignificantDigits: 1,
                          minimumFractionDigits: 0,
                          maximumSignificantDigits: 10,
                          maximumFractionDigits: 10,
                          style: "decimal",
                          useGrouping: true,
                        })
                      }}
                    </span>
                    <span class="amount-unit">
                      {{ item.stock_item_unit_of_measurement_abbreviation }}
                    </span>
                  </div>
                </template>
              </v-data-table>
            </b-overlay>
          </div>
        </div>

        <!-- Error Messages -->
        <ErrorsMessages :errors="errors" />
      </div>
    </div>
  </div>
</template>

<script>
import ErrorsMessages from "../Components/ErrorsMessages.vue";
import api from "../../services/api";
import moment from "moment";

export default {
  name: "Stocks",
  components: {
    ErrorsMessages,
  },
  data() {
    return {
      status_resident: "ATIVO",
      errors: [],
      loading: false,
      stock_item_types: [],
      stock_groups: [],
      stock_item_categories: [],
      raw_residents: [],
      stock_item_type_id: "",
      stock_group_id: "",
      stock_item_category_id: "",
      search: "",
      resident_id: "TODOS",
      options: {},
      headers: [
        { text: "PROPRIETÁRIO", value: "owner" },
        { text: "ITEM", value: "name" },
        { text: "GRUPO", value: "stock_group_name" },
        { text: "QUANTIDADE", value: "amount" },
      ],
      stocked_items: [],
    };
  },
  created() {
    this.retrieve();
  },
  computed: {
    types() {
      return [{ id: "", name: "TODOS" }, ...this.stock_item_types];
    },
    groups() {
      return [{ id: "", name: "TODOS" }, ...this.stock_groups];
    },
    categories() {
      return [{ id: "", name: "TODAS" }, ...this.stock_item_categories];
    },
    residents() {
      return [
        { id: "TODOS", name: "TODOS" },
        { id: "", name: "EMPRESA" },
        ...this.raw_residents,
      ];
    },
  },
  methods: {
    moment,

    handleChange() {
      this.options.page = 1;
      this.getStockedItems();
    },

    getItemIcon(item) {
      if (item.stock_item_type_value === "MEDICINE") {
        return "fas fa-pills";
      }
      return "fas fa-box";
    },

    getGroupBadgeClass(groupName) {
      const name = groupName?.toLowerCase() || "";
      if (name.includes("medicamento")) return "group-medicine";
      if (name.includes("higiene")) return "group-hygiene";
      if (name.includes("alimentar")) return "group-food";
      if (name.includes("limpeza")) return "group-cleaning";
      return "group-default";
    },

    getAmountClass(amount) {
      if (amount <= 0) return "amount-zero";
      if (amount < 10) return "amount-low";
      if (amount < 50) return "amount-medium";
      return "amount-high";
    },

    handleChangeStatusResident() {
      this.stocked_items = [];
      this.getResidents();
    },

    async retrieve() {
      if (this.errors.length === 0) await this.getStockItemTypes();
      if (this.errors.length === 0) await this.getStockGroups();
      if (this.errors.length === 0) await this.getStockItemCategories();
      if (this.errors.length === 0) await this.getResidents();
      if (this.errors.length === 0) await this.getStockedItems();
    },

    async getStockedItems() {
      try {
        this.loading = true;

        const params = {
          stock_item_type_id: this.stock_item_type_id,
          stock_group_id: this.stock_group_id,
          stock_item_category_id: this.stock_item_category_id,
          resident_id: this.resident_id,
        };
        const response = await api.get("/rest-home/stock-paginate", { params });
        const { error, errors, stocked_items } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          for (let item of stocked_items) {
            item.name =
              item.stock_item_type_value == "MEDICINE"
                ? item.medicine_name
                : item.product_name;
          }
          this.stocked_items = stocked_items;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async getStockItemTypes() {
      try {
        const response = await api.get("/rest-home/stock/items/types");
        const { error, errors, stock_item_types } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.stock_item_types = stock_item_types;
        }
      } catch (error) {
        console.error(error);
      }
    },

    async getStockGroups() {
      try {
        const response = await api.get("/rest-home/stock/groups");
        const { error, errors, stock_groups } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.stock_groups = stock_groups;
        }
      } catch (error) {
        console.error(error);
      }
    },

    async getStockItemCategories() {
      try {
        const response = await api.get("/rest-home/stock/items/categories");
        const { error, errors, stock_item_categories } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          for (let category of stock_item_categories) {
            category.name = `${category.name} (${category.stock_item_type_name})`;
          }
          this.stock_item_categories = stock_item_categories;
        }
      } catch (error) {
        console.error(error);
      }
    },

    async getResidents() {
      try {
        let { status_resident } = this;
        let params = { status_resident };
        const response = await api.get("/rest-home/residents", { params });
        const { error, errors, residents } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.raw_residents = residents;
        }
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<style scoped>
.stocks-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 24px 0;
}

.stocks-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header Section */
.stocks-header {
  background: white;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.header-content {
  text-align: center;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 0;
  font-size: 16px;
}

/* Content Section */
.stocks-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Filters Section */
.filters-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.filters-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.filters-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.filters-title i {
  color: #0f65b0;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  padding: 24px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.filter-icon {
  color: #6b7280;
  font-size: 14px;
}

/* Table Section */
.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.table-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.table-title i {
  color: #0f65b0;
}

.table-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.item-count {
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.loading-overlay {
  border-radius: 0 0 16px 16px;
  min-height: 200px;
}

/* No Results */
.no-results {
  text-align: center;
  padding: 60px 24px;
}

.no-results-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 20px;
}

.no-results-title {
  color: #1e293b;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.no-results-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
}

/* Table Item Styling */
.item-name-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.item-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
}

.item-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.item-name {
  font-weight: 500;
  color: #1e293b;
  font-size: 14px;
}

.item-type {
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Group Badges */
.group-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.group-medicine {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #2563eb;
  border: 1px solid #60a5fa;
}

.group-hygiene {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.group-food {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  color: #d97706;
  border: 1px solid #f59e0b;
}

.group-cleaning {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #4338ca;
  border: 1px solid #818cf8;
}

.group-default {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #475569;
  border: 1px solid #cbd5e1;
}

/* Amount Cell */
.amount-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.amount-value {
  font-weight: 600;
  font-size: 16px;
}

.amount-zero {
  color: #ef4444;
}

.amount-low {
  color: #f59e0b;
}

.amount-medium {
  color: #3b82f6;
}

.amount-high {
  color: #10b981;
}

.amount-unit {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .filters-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .stocks-container {
    padding: 0 16px;
  }

  .stocks-header {
    padding: 24px;
    margin-bottom: 16px;
  }

  .page-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 20px;
  }

  .table-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
    text-align: center;
  }

  .item-name-cell {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  .item-icon {
    width: 36px;
    height: 36px;
    font-size: 14px;
    align-self: center;
  }

  .amount-cell {
    align-items: center;
  }
}

@media (max-width: 480px) {
  .stocks-modern {
    padding: 16px 0;
  }

  .page-title {
    font-size: 20px;
  }

  .title-icon {
    font-size: 20px;
  }

  .filters-card,
  .table-card {
    border-radius: 12px;
  }

  .filters-header,
  .table-header {
    padding: 16px;
  }

  .filters-grid {
    padding: 16px;
  }

  .no-results {
    padding: 40px 16px;
  }

  .no-results-icon {
    font-size: 48px;
  }

  .no-results-title {
    font-size: 18px;
  }

  .no-results-text {
    font-size: 14px;
  }
}

/* Override Vuetify styles */
.modern-select >>> .v-input__control,
.modern-input >>> .v-input__control {
  border-radius: 8px !important;
}

.modern-select >>> .v-select__selection,
.modern-input >>> .v-text-field__slot input {
  font-weight: 500 !important;
}

.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 !important;
}

.modern-table >>> .v-data-table thead th {
  background-color: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.modern-table >>> .v-data-table tbody tr:hover {
  background-color: #f8fafc !important;
}

.modern-table >>> .v-data-footer {
  border-top: 1px solid #e5e7eb !important;
  background-color: #f8fafc !important;
}
</style>